import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, UtensilsCrossed, Menu as MenuIcon, X, ShieldCheck, AlertTriangle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Allergen SVG mini-icons (inline for public page) ─────────────────────────
const ALG_ICONS = {
  alg_gluten: { name: "Gluten", color: "#D97706" },
  alg_crustaceos: { name: "Crustaceos", color: "#DC2626" },
  alg_huevo: { name: "Huevo", color: "#F59E0B" },
  alg_pescado: { name: "Pescado", color: "#2563EB" },
  alg_cacahuetes: { name: "Cacahuetes", color: "#92400E" },
  alg_soja: { name: "Soja", color: "#65A30D" },
  alg_leche: { name: "Leche", color: "#3B82F6" },
  alg_frutos_cascara: { name: "Frutos cascara", color: "#A16207" },
  alg_apio: { name: "Apio", color: "#16A34A" },
  alg_mostaza: { name: "Mostaza", color: "#CA8A04" },
  alg_sesamo: { name: "Sesamo", color: "#78716C" },
  alg_sulfitos: { name: "Sulfitos", color: "#7C3AED" },
  alg_altramuces: { name: "Altramuces", color: "#0D9488" },
  alg_moluscos: { name: "Moluscos", color: "#6366F1" },
};

function AllergenDot({ algId, severity = "contains" }) {
  const info = ALG_ICONS[algId];
  if (!info) return null;
  return (
    <span
      title={`${info.name}${severity !== "contains" ? " (puede contener)" : ""}`}
      style={{
        display: "inline-block", width: 10, height: 10, borderRadius: "50%",
        background: info.color, opacity: severity === "contains" ? 1 : 0.5,
        border: severity === "may_contain" ? "1.5px dashed #fff" : "none",
      }}
    />
  );
}

function _isLightColorPublic(hex) {
  if (!hex || hex.length < 7) return true;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 140;
}

function _adjustBrightness(hex, percent) {
  if (!hex || hex.length < 7) return hex;
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);
  r = Math.min(255, Math.max(0, r + Math.round(r * percent / 100)));
  g = Math.min(255, Math.max(0, g + Math.round(g * percent / 100)));
  b = Math.min(255, Math.max(0, b + Math.round(b * percent / 100)));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

// ─── Theme definitions (must match WebsitePage.jsx) ──────────────────────────
const THEMES = {
  mediterraneo: {
    bg: "#FDFCF8", navBg: "#2D2A26", navText: "#FDFCF8", primary: "#E05D3A",
    text: "#2D2A26", muted: "#5C5855", border: "#E6E2DE",
    sectionAlt: "#F5EFE6", cardBg: "#FFFFFF",
    heroOverlay: "linear-gradient(to bottom, rgba(26,23,20,0.25) 0%, rgba(26,23,20,0.75) 100%)",
    heroFont: "'Fraunces', serif", bodyFont: "'Manrope', sans-serif",
    badge: { bg: "rgba(255,255,255,0.12)", border: "rgba(255,255,255,0.25)", text: "rgba(255,255,255,0.8)" },
    btn: { bg: "#E05D3A", text: "#fff", hover: "#c94d2e" },
    catActive: { bg: "#E05D3A", text: "#fff" },
    catInactive: { bg: "transparent", text: "#5C5855", border: "#E6E2DE" },
    priceColor: "#E05D3A",
  },
  moderno: {
    bg: "#0F0F0F", navBg: "#0F0F0F", navText: "#F5F5F5", primary: "#FF6B35",
    text: "#F5F5F5", muted: "#A0A0A0", border: "#2A2A2A",
    sectionAlt: "#1A1A1A", cardBg: "#1C1C1C",
    heroOverlay: "linear-gradient(to bottom, rgba(15,15,15,0.1) 0%, rgba(15,15,15,0.9) 100%)",
    heroFont: "'DM Serif Display', serif", bodyFont: "'DM Sans', sans-serif",
    badge: { bg: "rgba(255,107,53,0.15)", border: "rgba(255,107,53,0.4)", text: "#FF6B35" },
    btn: { bg: "#FF6B35", text: "#fff", hover: "#e55c26" },
    catActive: { bg: "#FF6B35", text: "#fff" },
    catInactive: { bg: "transparent", text: "#A0A0A0", border: "#2A2A2A" },
    priceColor: "#FF6B35",
  },
  elegante: {
    bg: "#F8F5F0", navBg: "#1A1A1A", navText: "#F8F5F0", primary: "#8B6914",
    text: "#1A1A1A", muted: "#6B6560", border: "#DDD8D0",
    sectionAlt: "#F0EBE2", cardBg: "#FFFFFF",
    heroOverlay: "linear-gradient(to bottom, rgba(26,26,26,0.15) 0%, rgba(26,26,26,0.85) 100%)",
    heroFont: "'Playfair Display', serif", bodyFont: "'Lato', sans-serif",
    badge: { bg: "rgba(139,105,20,0.12)", border: "rgba(139,105,20,0.3)", text: "#8B6914" },
    btn: { bg: "#8B6914", text: "#fff", hover: "#755810" },
    catActive: { bg: "#8B6914", text: "#fff" },
    catInactive: { bg: "transparent", text: "#6B6560", border: "#DDD8D0" },
    priceColor: "#8B6914",
  },
  minimalista: {
    bg: "#FFFFFF", navBg: "#FFFFFF", navText: "#2D2D2D", primary: "#2D2D2D",
    text: "#2D2D2D", muted: "#737373", border: "#E5E5E5",
    sectionAlt: "#F7F7F7", cardBg: "#FAFAFA",
    heroOverlay: "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.7) 100%)",
    heroFont: "'Cormorant Garamond', serif", bodyFont: "'Inter', sans-serif",
    badge: { bg: "rgba(255,255,255,0.15)", border: "rgba(255,255,255,0.3)", text: "rgba(255,255,255,0.9)" },
    btn: { bg: "#2D2D2D", text: "#fff", hover: "#1a1a1a" },
    catActive: { bg: "#2D2D2D", text: "#fff" },
    catInactive: { bg: "transparent", text: "#737373", border: "#E5E5E5" },
    priceColor: "#2D2D2D",
  },
  rustico: {
    bg: "#F5EBD8", navBg: "#3D2B1F", navText: "#F5EBD8", primary: "#8B4513",
    text: "#3D2B1F", muted: "#7A6555", border: "#D9C8B0",
    sectionAlt: "#FEF8EF", cardBg: "#FFF9F0",
    heroOverlay: "linear-gradient(to bottom, rgba(61,43,31,0.15) 0%, rgba(61,43,31,0.8) 100%)",
    heroFont: "'Crimson Text', serif", bodyFont: "'Source Serif 4', serif",
    badge: { bg: "rgba(255,255,255,0.1)", border: "rgba(255,255,255,0.2)", text: "rgba(255,255,255,0.85)" },
    btn: { bg: "#8B4513", text: "#fff", hover: "#723610" },
    catActive: { bg: "#8B4513", text: "#fff" },
    catInactive: { bg: "transparent", text: "#7A6555", border: "#D9C8B0" },
    priceColor: "#8B4513",
  },
  casual: {
    bg: "#1A1A1A", navBg: "#111111", navText: "#F0EBE3", primary: "#C8A97E",
    text: "#F0EBE3", muted: "#9A9590", border: "#333333",
    sectionAlt: "#222222", cardBg: "#262626",
    heroOverlay: "linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.85) 100%)",
    heroFont: "'DM Serif Display', serif", bodyFont: "'DM Sans', sans-serif",
    badge: { bg: "rgba(200,169,126,0.15)", border: "rgba(200,169,126,0.4)", text: "#C8A97E" },
    btn: { bg: "#C8A97E", text: "#1A1A1A", hover: "#b89a6f" },
    catActive: { bg: "#C8A97E", text: "#1A1A1A" },
    catInactive: { bg: "transparent", text: "#9A9590", border: "#444" },
    priceColor: "#C8A97E",
    isCasual: true,
  },
  casual_fast: {
    bg: "#1C1C1E", navBg: "#141416", navText: "#F5F5F7", primary: "#FF5F2E",
    text: "#F5F5F7", muted: "#8E8E93", border: "#3A3A3C",
    sectionAlt: "#2C2C2E", cardBg: "#2C2C2E",
    heroOverlay: "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.88) 100%)",
    heroFont: "'DM Sans', sans-serif", bodyFont: "'DM Sans', sans-serif",
    badge: { bg: "rgba(255,95,46,0.15)", border: "rgba(255,95,46,0.4)", text: "#FF5F2E" },
    btn: { bg: "#FF5F2E", text: "#fff", hover: "#e54d1f" },
    catActive: { bg: "#FF5F2E", text: "#fff" },
    catInactive: { bg: "transparent", text: "#8E8E93", border: "#3A3A3C" },
    priceColor: "#FF5F2E",
    isCasualFast: true,
    isCasual: true,
  },
};

const HERO_FALLBACK = "https://images.unsplash.com/photo-1544148103-0773bf10d330?w=1600&q=80&auto=format&fit=crop";
const DISH_FALLBACK = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&q=80&auto=format&fit=crop";

const CUISINE_LABELS = {
  spanish: "Cocina Espanola", mediterranean: "Cocina Mediterranea", italian: "Cocina Italiana",
  mexican: "Cocina Mexicana", asian: "Cocina Asiatica", japanese: "Cocina Japonesa",
  american: "Cocina Americana", fusion: "Cocina de Fusion", tapas: "Tapas & Pintxos",
  vegetarian: "Cocina Vegetariana",
};

// ─── Responsive CSS ──────────────────────────────────────────────────────────
const RESPONSIVE_CSS = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(8px); } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes casualFastFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }

  [data-rl-header] { padding: 16px 40px !important; }
  [data-rl-nav-links] { display: flex; gap: 28px; align-items: center; }
  [data-rl-hamburger] { display: none !important; }
  [data-rl-mobile-menu] { display: none !important; }

  [data-rl-hero] { min-height: 92vh; }
  [data-rl-hero-content] { max-width: 900px; padding: 0 32px; }
  [data-rl-hero-logo] { width: 140px; height: 140px; }

  [data-rl-about] { padding: 100px 40px; }
  [data-rl-about-grid] { grid-template-columns: 1fr 1fr !important; gap: 64px; }

  [data-rl-menu] { padding: 100px 40px; }
  [data-rl-dishes-grid] { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)) !important; gap: 24px; }

  [data-rl-contact] { padding: 100px 40px; }
  [data-rl-loyalty] { padding: 100px 40px; }
  [data-rl-loyalty-grid] { grid-template-columns: 1fr 1fr !important; gap: 48px; }

  [data-rl-schedule-grid] { grid-template-columns: 1fr 1fr !important; gap: 12px; }

  @media (max-width: 768px) {
    [data-rl-header] { padding: 12px 16px !important; }
    [data-rl-nav-links] { display: none !important; }
    [data-rl-hamburger] { display: flex !important; }
    [data-rl-mobile-menu][data-open="true"] {
      display: flex !important; flex-direction: column; position: fixed;
      top: 0; left: 0; right: 0; bottom: 0; z-index: 200;
      align-items: center; justify-content: center; gap: 32px;
      animation: fadeIn 0.2s ease;
    }

    [data-rl-hero] { min-height: 80vh; }
    [data-rl-hero-content] { padding: 0 20px; }
    [data-rl-hero-logo] { width: 100px; height: 100px; }
    [data-rl-hero-scroll] { display: none !important; }

    [data-rl-about] { padding: 60px 20px; }
    [data-rl-about-grid] { grid-template-columns: 1fr !important; gap: 32px; }
    [data-rl-about-images] { order: -1; grid-template-columns: 1fr 1fr !important; gap: 8px; }

    [data-rl-menu] { padding: 60px 16px; }
    [data-rl-dishes-grid] { grid-template-columns: 1fr !important; gap: 16px; }
    [data-rl-cat-tabs] { gap: 6px !important; }
    [data-rl-cat-tabs] button { padding: 8px 14px !important; font-size: 0.8rem !important; }

    [data-rl-contact] { padding: 60px 20px; }
    [data-rl-loyalty] { padding: 60px 20px; }
    [data-rl-loyalty-grid] { grid-template-columns: 1fr !important; gap: 32px; }

    [data-rl-schedule-grid] { grid-template-columns: 1fr !important; gap: 8px; }
  }

  @media (max-width: 480px) {
    [data-rl-hero] { min-height: 75vh; }
    [data-rl-about-images] { grid-template-columns: 1fr 1fr !important; }
    [data-rl-dishes-grid] { grid-template-columns: 1fr !important; }
  }
`;

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <div style={{ background: "#FDFCF8", minHeight: "100vh", fontFamily: "Manrope, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "24px" }}>
      <div style={{ width: "64px", height: "64px", borderRadius: "50%", border: "3px solid #E05D3A", borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
      <p style={{ color: "#5C5855", fontSize: "0.9rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>Cargando...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ background: "#FDFCF8", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "Manrope, sans-serif", padding: "20px" }}>
      <UtensilsCrossed size={56} color="#E05D3A" style={{ marginBottom: "24px" }} />
      <h1 style={{ fontFamily: "Fraunces, serif", fontSize: "clamp(1.5rem, 5vw, 2.5rem)", color: "#2D2A26", marginBottom: "12px", textAlign: "center" }}>Restaurante no encontrado</h1>
      <p style={{ color: "#5C5855", textAlign: "center" }}>Esta pagina no esta disponible o no ha sido activada.</p>
    </div>
  );
}

// ─── Dish Card ─────────────────────────────────────────────────────────────────
function DishCard({ dish, t, onAllergenClick }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div
      data-testid={`dish-card-${dish.dish_id}`}
      style={{
        background: t.cardBg, borderRadius: "14px", overflow: "hidden",
        boxShadow: "0 2px 16px -4px rgba(0,0,0,0.08)", display: "flex",
        flexDirection: "column", transition: "transform 0.2s, box-shadow 0.2s",
        border: `1px solid ${t.border}`,
      }}
    >
      <div style={{ position: "relative", paddingTop: "60%", overflow: "hidden", background: t.sectionAlt }}>
        <img
          src={(!imgError && dish.image_url) ? dish.image_url : DISH_FALLBACK}
          alt={dish.name}
          loading="lazy"
          onError={() => setImgError(true)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s" }}
        />
        {dish.tags?.length > 0 && (
          <div style={{ position: "absolute", top: "10px", left: "10px" }}>
            <span style={{ background: t.primary, color: "#fff", fontSize: "0.7rem", fontWeight: 700, padding: "3px 10px", borderRadius: "100px" }}>
              {dish.tags[0]}
            </span>
          </div>
        )}
      </div>
      <div style={{ padding: "16px 18px 20px", flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
          <h3 style={{ fontFamily: t.heroFont, fontSize: "1.05rem", fontWeight: 600, color: t.text, lineHeight: 1.3, margin: 0 }}>{dish.name}</h3>
          {dish.price != null && (
            <span style={{ fontWeight: 700, color: t.priceColor, whiteSpace: "nowrap", flexShrink: 0, fontSize: "1rem" }}>{dish.price.toFixed(2)}&euro;</span>
          )}
        </div>
        {dish.description && (
          <p style={{ fontFamily: t.bodyFont, fontSize: "0.82rem", color: t.muted, lineHeight: 1.6, margin: 0 }}>{dish.description}</p>
        )}
        <button
          onClick={() => onAllergenClick?.(dish)}
          style={{
            marginTop: "auto", paddingTop: "8px", display: "flex", alignItems: "center", gap: "4px",
            background: "none", border: "none", cursor: "pointer", padding: "4px 0",
            fontSize: "0.72rem", color: t.muted, fontFamily: t.bodyFont,
          }}
          data-testid={`allergen-btn-${dish.dish_id}`}
        >
          <AlertTriangle size={12} style={{ opacity: 0.6 }} />
          Ver alergenos
        </button>
      </div>
    </div>
  );
}

// ─── Casual Theme: Dish Card (cut-out style with shadow, dark bg) ─────────────
function CasualDishCard({ dish, t, onAllergenClick }) {
  const [imgError, setImgError] = useState(false);
  const hasImage = !imgError && dish.image_url;
  return (
    <div
      data-testid={`dish-card-${dish.dish_id}`}
      style={{
        background: t.cardBg, borderRadius: "16px", overflow: "hidden",
        display: "flex", flexDirection: "column", alignItems: "center",
        padding: "24px 16px 20px", transition: "transform 0.25s",
        border: `1px solid ${t.border}`,
      }}
    >
      {hasImage ? (
        <div style={{ width: "140px", height: "140px", marginBottom: "16px", position: "relative" }}>
          <img
            src={dish.image_url}
            alt={dish.name}
            loading="lazy"
            onError={() => setImgError(true)}
            style={{
              width: "100%", height: "100%", objectFit: "contain",
              filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.5))",
              transition: "transform 0.3s",
            }}
          />
        </div>
      ) : (
        <div style={{
          width: "140px", height: "140px", marginBottom: "16px",
          borderRadius: "50%", background: t.sectionAlt,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <UtensilsCrossed size={32} color={t.muted} style={{ opacity: 0.4 }} />
        </div>
      )}
      <h3 style={{
        fontFamily: t.heroFont, fontSize: "0.95rem", fontWeight: 600,
        color: t.text, textAlign: "center", lineHeight: 1.3, margin: 0,
      }}>{dish.name}</h3>
      {dish.description && (
        <p style={{
          fontFamily: t.bodyFont, fontSize: "0.78rem", color: t.muted,
          textAlign: "center", lineHeight: 1.5, margin: "6px 0 0", maxWidth: "200px",
        }}>{dish.description}</p>
      )}
      {dish.price != null && (
        <span style={{
          fontWeight: 700, color: t.priceColor, fontSize: "1.05rem",
          marginTop: "10px", letterSpacing: "0.02em",
        }}>{dish.price.toFixed(2)}&euro;</span>
      )}
      {dish.tags?.length > 0 && (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center", marginTop: "8px" }}>
          {dish.tags.map((tag, i) => (
            <span key={i} style={{
              fontSize: "0.65rem", fontWeight: 600, padding: "2px 10px",
              borderRadius: "100px", background: t.badge.bg,
              border: `1px solid ${t.badge.border}`, color: t.badge.text,
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>{tag}</span>
          ))}
        </div>
      )}
      <button
        onClick={() => onAllergenClick?.(dish)}
        style={{
          marginTop: "10px", display: "flex", alignItems: "center", gap: "4px",
          background: "none", border: "none", cursor: "pointer",
          fontSize: "0.68rem", color: t.muted, fontFamily: t.bodyFont,
        }}
        data-testid={`allergen-btn-${dish.dish_id}`}
      >
        <AlertTriangle size={11} style={{ opacity: 0.5 }} />
        Alergenos
      </button>
    </div>
  );
}

// ─── Casual Theme: Category Hero Banner ───────────────────────────────────────
function CasualCategoryHero({ category, image, t }) {
  return (
    <div style={{
      position: "relative", width: "100%", height: "280px",
      borderRadius: "20px", overflow: "hidden", marginBottom: "32px",
    }}>
      {image ? (
        <img
          src={image}
          alt={category}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }} />
      )}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%)",
        display: "flex", alignItems: "flex-end", padding: "32px",
      }}>
        <h2 style={{
          fontFamily: t.heroFont, fontSize: "clamp(1.5rem, 4vw, 2.2rem)",
          color: "#fff", margin: 0, letterSpacing: "-0.01em",
        }}>{category}</h2>
      </div>
    </div>
  );
}

// ─── Casual Fast Theme: Dish Card with floating/motion animation ──────────────
function CasualFastDishCard({ dish, t, index, onAllergenClick }) {
  const [imgError, setImgError] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef(null);
  const hasImage = !imgError && dish.image_url;

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const delay = (index % 4) * 0.12;

  return (
    <div
      ref={cardRef}
      data-testid={`dish-card-${dish.dish_id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: t.cardBg,
        borderRadius: "20px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "28px 16px 20px",
        border: `1px solid ${hovered ? t.primary + "40" : t.border}`,
        transform: visible
          ? (hovered ? "translateY(-8px) scale(1.03)" : "translateY(0) scale(1)")
          : "translateY(40px) scale(0.95)",
        opacity: visible ? 1 : 0,
        transition: `transform 0.5s cubic-bezier(0.23, 1, 0.32, 1) ${delay}s, opacity 0.5s ease ${delay}s, border-color 0.3s`,
        boxShadow: hovered ? `0 20px 40px -10px ${t.primary}25` : "0 4px 20px -6px rgba(0,0,0,0.3)",
        cursor: "default",
        position: "relative",
      }}
    >
      {/* Floating image with bounce */}
      {hasImage ? (
        <div style={{
          width: "160px", height: "160px", marginBottom: "16px", position: "relative",
          animation: visible ? `casualFastFloat 3s ease-in-out ${delay + 0.5}s infinite` : "none",
        }}>
          <img
            src={dish.image_url}
            alt={dish.name}
            loading="lazy"
            onError={() => setImgError(true)}
            style={{
              width: "100%", height: "100%", objectFit: "contain",
              filter: `drop-shadow(0 12px 28px rgba(0,0,0,0.6))`,
              transform: hovered ? "scale(1.1) rotate(-2deg)" : "scale(1) rotate(0deg)",
              transition: "transform 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
            }}
          />
        </div>
      ) : (
        <div style={{
          width: "160px", height: "160px", marginBottom: "16px",
          borderRadius: "50%", background: t.sectionAlt,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <UtensilsCrossed size={36} color={t.muted} style={{ opacity: 0.3 }} />
        </div>
      )}

      {/* Name */}
      <h3 style={{
        fontFamily: t.heroFont, fontSize: "1rem", fontWeight: 700,
        color: t.text, textAlign: "center", lineHeight: 1.3, margin: 0,
      }}>{dish.name}</h3>

      {/* Description */}
      {dish.description && (
        <p style={{
          fontFamily: t.bodyFont, fontSize: "0.78rem", color: t.muted,
          textAlign: "center", lineHeight: 1.5, margin: "6px 0 0",
          maxWidth: "220px", display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>{dish.description}</p>
      )}

      {/* Price */}
      {dish.price != null && (
        <span style={{
          fontWeight: 800, color: t.priceColor, fontSize: "1.15rem",
          marginTop: "10px", letterSpacing: "-0.01em",
        }}>{dish.price.toFixed(2)}&euro;</span>
      )}

      {/* Tags */}
      {dish.tags?.length > 0 && (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center", marginTop: "8px" }}>
          {dish.tags.map((tag, i) => (
            <span key={i} style={{
              fontSize: "0.62rem", fontWeight: 700, padding: "3px 10px",
              borderRadius: "100px", background: t.badge.bg,
              border: `1px solid ${t.badge.border}`, color: t.badge.text,
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}>{tag}</span>
          ))}
        </div>
      )}

      {/* Allergen link */}
      <button
        onClick={() => onAllergenClick?.(dish)}
        style={{
          marginTop: "10px", display: "flex", alignItems: "center", gap: "4px",
          background: "none", border: "none", cursor: "pointer",
          fontSize: "0.68rem", color: t.muted, fontFamily: t.bodyFont,
          opacity: 0.7, transition: "opacity 0.2s",
        }}
        data-testid={`allergen-btn-${dish.dish_id}`}
      >
        <AlertTriangle size={11} />
        Alergenos
      </button>
    </div>
  );
}

// ─── WhatsApp floating button ─────────────────────────────────────────────────
function WhatsAppButton({ phone, t }) {
  if (!phone) return null;
  const clean = phone.replace(/\D/g, "");
  return (
    <a
      href={`https://wa.me/${clean}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-btn"
      style={{
        position: "fixed", bottom: "24px", right: "24px", zIndex: 90,
        width: "56px", height: "56px", borderRadius: "50%",
        background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 16px rgba(37,211,102,0.4)", transition: "transform 0.2s",
      }}
      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
      onMouseLeave={e => e.currentTarget.style.transform = ""}
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.612.638l4.725-1.527A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.24 0-4.308-.727-5.988-1.96l-.418-.312-3.266 1.056 1.02-3.146-.341-.543A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
      </svg>
    </a>
  );
}

// ─── Schedule section ─────────────────────────────────────────────────────────
function ScheduleSection({ schedule, t }) {
  if (!schedule || Object.keys(schedule).length === 0) return null;
  const DAYS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
  const DAY_LABELS = { lunes: "Lunes", martes: "Martes", miercoles: "Miercoles", jueves: "Jueves", viernes: "Viernes", sabado: "Sabado", domingo: "Domingo" };
  return (
    <div data-rl-schedule-grid style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "32px", maxWidth: "500px", margin: "32px auto 0" }}>
      {DAYS.map(day => {
        const val = schedule[day];
        return (
          <div key={day} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderRadius: "10px", background: `${t.primary}08`, border: `1px solid ${t.border}` }}>
            <span style={{ fontFamily: t.bodyFont, fontWeight: 600, fontSize: "0.85rem", color: t.text }}>{DAY_LABELS[day] || day}</span>
            <span style={{ fontFamily: t.bodyFont, fontSize: "0.85rem", color: val ? t.muted : t.primary }}>
              {val || "Cerrado"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function RestaurantLandingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loyaltyQR, setLoyaltyQR] = useState(null);
  const [showAllRewards, setShowAllRewards] = useState(false);
  const [customerProgress, setCustomerProgress] = useState(null); // {points, customer_name, next_reward}
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [allergenCache, setAllergenCache] = useState({});
  const [allergenModalDish, setAllergenModalDish] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/public/r/${slug}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => {
        setData(d);
        if (d.menus?.length > 0) {
          setActiveMenu(d.menus[0].menu_id);
          setActiveCategory(d.menus[0].categories?.[0]?.category_id || null);
        }
        setLoading(false);
        if (d.restaurant?.restaurant_id) {
          const rid = d.restaurant.restaurant_id;
          fetch(`${API_URL}/api/public/loyalty/${rid}`)
            .then(r => r.ok ? r.json() : null)
            .then(loyData => {
              if (loyData?.config) {
                setLoyaltyInfo(loyData);
                const frontendUrl = encodeURIComponent(window.location.origin);
                fetch(`${API_URL}/api/public/loyalty/${rid}/qr-image?frontend_url=${frontendUrl}`)
                  .then(r => r.ok ? r.json() : null)
                  .then(qrData => { if (qrData) setLoyaltyQR(qrData); })
                  .catch(() => {});
              }
            })
            .catch(() => {});
          fetch(`${API_URL}/api/public/restaurants/${rid}/social-accounts`)
            .then(r => r.ok ? r.json() : [])
            .then(accs => setSocialAccounts(accs || []))
            .catch(() => {});
        }
      })
      .catch(e => { setError(e); setLoading(false); });
  }, [slug]);

  // Load customer's loyalty progress when the modal opens (if a customer token
  // is cached locally — happens when they came via the portal in the same tab
  // or kept the token in localStorage).
  useEffect(() => {
    if (!showAllRewards || !data?.restaurant?.restaurant_id || !loyaltyInfo?.rewards) return;
    if (customerProgress !== null) return;
    const rid = data.restaurant.restaurant_id;
    const token = sessionStorage.getItem(`loyalty_token_${rid}`)
      || localStorage.getItem(`loyalty_token_${rid}`);
    const customerId = sessionStorage.getItem(`loyalty_customer_${rid}`)
      || localStorage.getItem(`loyalty_customer_${rid}`);
    if (!token || !customerId) return;
    fetch(`${API_URL}/api/public/loyalty/${rid}/customer/${customerId}?token=${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : null)
      .then(card => {
        if (!card?.customer) return;
        const points = card.customer.points || 0;
        // Next reward: cheapest reward they can't yet redeem
        const sorted = [...loyaltyInfo.rewards].sort((a, b) => (a.points_required || 0) - (b.points_required || 0));
        const next = sorted.find(r => (r.points_required || 0) > points);
        setCustomerProgress({
          points,
          customer_name: card.customer.name || null,
          next_reward: next || null,
        });
      })
      .catch(() => {});
  }, [showAllRewards, data, loyaltyInfo, customerProgress]);

  // Fetch allergen data for a dish on demand
  const openAllergenModal = async (dish) => {
    setAllergenModalDish(dish);
    if (allergenCache[dish.dish_id]) return;
    try {
      const rid = data?.restaurant?.restaurant_id;
      if (!rid) return;
      const res = await fetch(`${API_URL}/api/public/restaurants/${rid}/dishes/${dish.dish_id}/allergens`);
      if (res.ok) {
        const algData = await res.json();
        if (algData && !algData.error) {
          setAllergenCache(prev => ({ ...prev, [dish.dish_id]: algData }));
        } else {
          // Dish found but no allergen data
          setAllergenCache(prev => ({ ...prev, [dish.dish_id]: { contains: [], may_contain: [], traces: [], review_status: "needs_review", confidence_score: 0, explanations: [] } }));
        }
      } else {
        // API error — show empty state
        setAllergenCache(prev => ({ ...prev, [dish.dish_id]: { contains: [], may_contain: [], traces: [], review_status: "needs_review", confidence_score: 0, explanations: [] } }));
      }
    } catch {
      setAllergenCache(prev => ({ ...prev, [dish.dish_id]: { contains: [], may_contain: [], traces: [], review_status: "needs_review", confidence_score: 0, explanations: [] } }));
    }
  };

  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) setMobileMenuOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (loading) return <LoadingSkeleton />;
  if (error || !data) return <NotFound />;

  const { restaurant, menus } = data;
  const content = restaurant.website_content || {};
  const features = data.website_features || {};
  const styleKey = content.style || "mediterraneo";
  const baseT = THEMES[styleKey] || THEMES.mediterraneo;

  // Apply bg_mode override from website content
  const t = { ...baseT };
  const bgMode = content.bg_mode;
  if (bgMode === "light") {
    t.bg = "#FFFFFF"; t.text = "#1A1A1A"; t.muted = "#6B7280";
    t.sectionAlt = "#FAFAFA"; t.cardBg = "#FFFFFF"; t.border = "#E5E7EB";
    t.navBg = "#FFFFFFEE"; t.navText = "#1A1A1A";
    t.catInactive = { ...t.catInactive, text: "#6B7280", border: "#E5E7EB" };
    t.priceColor = t.primary;
    if (t.badge) t.badge = { bg: `${t.primary}15`, border: `${t.primary}30`, text: t.primary };
  } else if (bgMode === "dark") {
    t.bg = "#111111"; t.text = "#F5F5F7"; t.muted = "#9CA3AF";
    t.sectionAlt = "#1A1A1A"; t.cardBg = "#1E1E1E"; t.border = "#333";
    t.navBg = "#0A0A0A"; t.navText = "#F5F5F7";
    t.catInactive = { ...t.catInactive, text: "#9CA3AF", border: "#444" };
  } else if (bgMode === "custom" && content.custom_bg_color) {
    const hex = content.custom_bg_color;
    const isLight = _isLightColorPublic(hex);
    t.bg = hex;
    t.text = isLight ? "#1A1A1A" : "#F5F5F7";
    t.muted = isLight ? "#6B7280" : "#9CA3AF";
    t.sectionAlt = isLight ? "#FAFAFA" : _adjustBrightness(hex, 10);
    t.cardBg = isLight ? "#FFFFFF" : _adjustBrightness(hex, 8);
    t.border = isLight ? "#E5E7EB" : _adjustBrightness(hex, 20);
    t.navBg = isLight ? "#FFFFFFDD" : _adjustBrightness(hex, -5);
    t.navText = t.text;
    t.catInactive = { ...t.catInactive, text: t.muted, border: t.border };
    t.priceColor = t.primary;
    if (isLight && t.badge) {
      t.badge = { bg: `${t.primary}15`, border: `${t.primary}30`, text: t.primary };
    }
  }

  const currentMenu = menus.find(m => m.menu_id === activeMenu) || menus[0];
  const allCategories = currentMenu?.categories || [];
  const activeCatData = activeCategory
    ? allCategories.find(c => c.category_id === activeCategory)
    : allCategories[0];

  const cuisineLabel = CUISINE_LABELS[restaurant.cuisine_type] || restaurant.cuisine_type || "";

  const fontImports = {
    mediterraneo: "https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,600;1,300;1,600&family=Manrope:wght@400;500;600&display=swap",
    moderno: "https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600&display=swap",
    elegante: "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Lato:wght@400;500;700&display=swap",
    minimalista: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Inter:wght@400;500;600&display=swap",
    rustico: "https://fonts.googleapis.com/css2?family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Source+Serif+4:wght@400;500;600&display=swap",
  };

  const scrollToMenu = () => {
    setMobileMenuOpen(false);
    menuRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToContact = () => {
    setMobileMenuOpen(false);
    document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
  };

  const showSchedule = features.show_schedule && content.schedule;
  const showWhatsapp = features.show_whatsapp && restaurant.phone;
  const hideBranding = features.hide_branding;

  return (
    <div style={{ background: t.bg, fontFamily: t.bodyFont, color: t.text, minHeight: "100vh" }}>
      <style>{RESPONSIVE_CSS}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="stylesheet" href={fontImports[styleKey]} />
      {content.meta_title && <title>{content.meta_title}</title>}

      {/* ── Sticky Header ──────────────────────────────────────────────── */}
      <header data-testid="restaurant-header" data-rl-header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: headerScrolled ? (styleKey === "moderno" ? "rgba(15,15,15,0.95)" : `${t.bg}F0`) : "transparent",
        backdropFilter: headerScrolled ? "blur(14px)" : "none",
        borderBottom: headerScrolled ? `1px solid ${t.border}` : "none",
        boxShadow: headerScrolled ? "0 2px 16px rgba(0,0,0,0.08)" : "none",
        transition: "all 0.3s ease",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {restaurant.logo_url ? (
            <img src={restaurant.logo_url} alt={restaurant.name} style={{ width: "38px", height: "38px", borderRadius: "8px", objectFit: "cover" }} />
          ) : (
            <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: t.primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <UtensilsCrossed size={18} color="#fff" />
            </div>
          )}
          <span style={{ fontFamily: t.heroFont, fontWeight: 600, fontSize: "1.2rem", color: headerScrolled ? t.text : "#fff" }}>
            {restaurant.name}
          </span>
        </div>

        {/* Desktop nav */}
        <nav data-rl-nav-links>
          <a href="#menu" onClick={e => { e.preventDefault(); scrollToMenu(); }}
            style={{ fontSize: "0.875rem", fontWeight: 500, color: headerScrolled ? t.muted : "rgba(255,255,255,0.85)", textDecoration: "none" }}>
            Menu
          </a>
          {restaurant.address && (
            <a href="#contacto" onClick={e => { e.preventDefault(); scrollToContact(); }}
              style={{ fontSize: "0.875rem", fontWeight: 500, color: headerScrolled ? t.muted : "rgba(255,255,255,0.85)", textDecoration: "none" }}>
              Contacto
            </a>
          )}
          <a href="#menu" onClick={e => { e.preventDefault(); scrollToMenu(); }}
            style={{ padding: "8px 20px", borderRadius: "100px", background: t.btn.bg, color: t.btn.text, fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", transition: "background 0.2s" }}>
            Ver carta
          </a>
        </nav>

        {/* Hamburger */}
        <button
          data-rl-hamburger
          data-testid="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "8px", color: headerScrolled ? t.text : "#fff", zIndex: 201 }}
        >
          {mobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
        </button>
      </header>

      {/* ── Mobile Menu Overlay ────────────────────────────────────────── */}
      <div
        data-rl-mobile-menu
        data-open={mobileMenuOpen ? "true" : "false"}
        style={{ background: t.navBg + "F5", backdropFilter: "blur(20px)" }}
      >
        <button onClick={() => setMobileMenuOpen(false)} style={{ position: "absolute", top: "16px", right: "16px", background: "none", border: "none", color: t.navText, cursor: "pointer" }}>
          <X size={28} />
        </button>
        <a href="#menu" onClick={e => { e.preventDefault(); scrollToMenu(); }}
          style={{ fontFamily: t.heroFont, fontSize: "1.5rem", color: t.navText, textDecoration: "none", fontWeight: 600 }}>
          Menu
        </a>
        {restaurant.address && (
          <a href="#contacto" onClick={e => { e.preventDefault(); scrollToContact(); }}
            style={{ fontFamily: t.heroFont, fontSize: "1.5rem", color: t.navText, textDecoration: "none", fontWeight: 600 }}>
            Contacto
          </a>
        )}
        <button onClick={scrollToMenu}
          style={{ padding: "14px 40px", borderRadius: "100px", background: t.btn.bg, color: t.btn.text, border: "none", fontSize: "1rem", fontWeight: 600, cursor: "pointer" }}>
          Ver carta
        </button>
      </div>

      {/* ── Ordered sections ──────────────────────────────────────────── */}
      {(content.sections_order || ["hero", "about", "specialties"]).map(key => {
        if (key === "hero") return (
          <section key="hero" data-testid="hero-section" data-rl-hero style={{
            position: "relative", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${content.hero_bg_url || HERO_FALLBACK})`, backgroundSize: "cover", backgroundPosition: "center", opacity: styleKey === "moderno" ? 0.25 : 0.4 }} />
            <div style={{ position: "absolute", inset: 0, background: t.heroOverlay }} />
            <div data-rl-hero-content style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
              {restaurant.logo_url && (
                <div style={{ display: "flex", justifyContent: "center", marginBottom: "32px" }}>
                  <img data-rl-hero-logo src={restaurant.logo_url} alt={restaurant.name} style={{ objectFit: "contain", borderRadius: "24px", boxShadow: "0 8px 40px rgba(0,0,0,0.35)", border: "2px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.06)", backdropFilter: "blur(4px)" }} />
                </div>
              )}
              {cuisineLabel && (
                <div style={{ display: "inline-block", marginBottom: "24px", padding: "6px 18px", borderRadius: "100px", border: `1px solid ${t.badge.border}`, background: t.badge.bg, fontSize: "0.78rem", letterSpacing: "0.15em", textTransform: "uppercase", color: t.badge.text, fontWeight: 500 }}>
                  {cuisineLabel}
                </div>
              )}
              <h1 style={{ fontFamily: t.heroFont, fontWeight: styleKey === "elegante" ? 400 : 300, fontStyle: "italic", fontSize: "clamp(2.2rem, 8vw, 6.5rem)", lineHeight: 1.1, color: "#FDFCF8", marginBottom: "20px", textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}>
                {content.hero_title || restaurant.name}
              </h1>
              <p style={{ fontFamily: t.bodyFont, fontSize: "clamp(0.95rem, 2vw, 1.3rem)", color: "rgba(255,255,255,0.82)", maxWidth: "600px", margin: "0 auto 40px", lineHeight: 1.6 }}>
                {content.hero_subtitle || cuisineLabel}
              </p>
              <button onClick={scrollToMenu}
                style={{ padding: "14px 40px", borderRadius: "100px", background: t.btn.bg, color: t.btn.text, border: "none", fontSize: "1rem", fontWeight: 600, cursor: "pointer", letterSpacing: "0.05em", transition: "background 0.2s, transform 0.2s" }}>
                {content.cta_text || "Descubrir la carta"}
              </button>
            </div>
            <div data-rl-hero-scroll style={{ position: "absolute", bottom: "40px", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", animation: "bounce 2s infinite" }}>
              <div style={{ width: "1px", height: "48px", background: "rgba(255,255,255,0.4)" }} />
            </div>
          </section>
        );

        if (key === "about" && content.about_text && features.show_about) return (
          <section key="about" data-rl-about style={{ background: t.sectionAlt }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
              <div data-rl-about-grid style={{ display: "grid", alignItems: "center" }}>
                <div>
                  <div style={{ display: "inline-block", width: "40px", height: "3px", background: t.primary, marginBottom: "24px", borderRadius: "2px" }} />
                  <h2 style={{ fontFamily: t.heroFont, fontStyle: "italic", fontWeight: styleKey === "elegante" ? 400 : 300, fontSize: "clamp(1.8rem, 4vw, 3rem)", color: t.text, marginBottom: "24px", lineHeight: 1.2 }}>
                    {content.about_title || "Sobre Nosotros"}
                  </h2>
                  {content.about_text.split("\n").filter(p => p.trim()).map((paragraph, i) => (
                    <p key={`about-p-${i}`} style={{ fontFamily: t.bodyFont, fontSize: "1rem", color: t.muted, lineHeight: 1.8, marginBottom: "16px" }}>{paragraph}</p>
                  ))}
                </div>
                <div data-rl-about-images style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  {(currentMenu?.categories?.flatMap(c => c.dishes || []).slice(0, 4) || []).map((dish, i) => (
                    <div key={dish.dish_id || `about-dish-${i}`} style={{ paddingTop: "100%", position: "relative", borderRadius: "12px", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                      <img src={dish.image_url || DISH_FALLBACK} alt={dish.name} loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.src = DISH_FALLBACK; }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );

        if (key === "specialties") return (
          <section key="specialties" ref={menuRef} id="menu" data-rl-menu style={{ background: t.bg }} data-testid="menu-section">
            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: "60px" }}>
                <div style={{ display: "inline-block", width: "40px", height: "3px", background: t.primary, marginBottom: "20px", borderRadius: "2px" }} />
                <h2 style={{ fontFamily: t.heroFont, fontStyle: "italic", fontWeight: styleKey === "elegante" ? 400 : 300, fontSize: "clamp(1.8rem, 4vw, 3rem)", color: t.text, marginBottom: "12px" }}>
                  {content.specialties_title || "Nuestra Carta"}
                </h2>
                {content.specialties_intro && (
                  <p style={{ fontFamily: t.bodyFont, fontSize: "1.05rem", color: t.muted, maxWidth: "600px", margin: "0 auto", lineHeight: 1.6 }}>
                    {content.specialties_intro}
                  </p>
                )}
              </div>

              {/* Casual theme: horizontal category nav + all-in-one scroll */}
              {t.isCasual ? (
                <>
                  {/* Sticky category navigation pills */}
                  {allCategories.length > 0 && (
                    <div style={{
                      display: "flex", gap: "10px", justifyContent: "center",
                      flexWrap: "wrap", marginBottom: "48px",
                      position: "sticky", top: "70px", zIndex: 20,
                      background: t.bg, padding: "16px 0",
                    }} data-testid="casual-category-nav">
                      {allCategories.map(cat => {
                        const isActive = cat.category_id === (activeCategory || allCategories[0]?.category_id);
                        return (
                          <button key={cat.category_id}
                            onClick={() => {
                              setActiveCategory(cat.category_id);
                              document.getElementById(`casual-cat-${cat.category_id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }}
                            style={{
                              padding: "10px 22px", borderRadius: "100px",
                              border: `1px solid ${isActive ? t.primary : t.border}`,
                              background: isActive ? t.primary : "transparent",
                              color: isActive ? t.bg : t.muted,
                              fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
                              transition: "all 0.2s",
                            }}>
                            {cat.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {/* Render all categories with hero banners */}
                  {allCategories.map(cat => (
                    <div key={cat.category_id} id={`casual-cat-${cat.category_id}`} style={{ marginBottom: "64px", scrollMarginTop: "140px" }}>
                      <CasualCategoryHero
                        category={cat.name}
                        image={cat.dishes?.[0]?.image_url || null}
                        t={t}
                      />
                      {cat.description && (
                        <p style={{
                          fontFamily: t.bodyFont, fontSize: "0.95rem", color: t.muted,
                          textAlign: "center", maxWidth: "600px", margin: "-16px auto 32px",
                          lineHeight: 1.6,
                        }}>{cat.description}</p>
                      )}
                      {cat.dishes?.length > 0 ? (
                        <div data-rl-dishes-grid style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "20px" }}>
                          {cat.dishes.map((dish, idx) => t.isCasualFast
                            ? <CasualFastDishCard key={dish.dish_id} dish={dish} t={t} index={idx} onAllergenClick={openAllergenModal} />
                            : <CasualDishCard key={dish.dish_id} dish={dish} t={t} onAllergenClick={openAllergenModal} />
                          )}
                        </div>
                      ) : (
                        <div style={{ textAlign: "center", padding: "40px 20px", color: t.muted }}>
                          <p style={{ opacity: 0.5 }}>Sin platos en esta categoria</p>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <>
              {menus.length > 1 && (
                <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginBottom: "40px", flexWrap: "wrap" }}>
                  {menus.map(menu => {
                    const isActive = menu.menu_id === activeMenu;
                    return (
                      <button key={menu.menu_id} onClick={() => { setActiveMenu(menu.menu_id); setActiveCategory(menu.categories?.[0]?.category_id || null); }}
                        style={{ padding: "10px 24px", borderRadius: "100px", border: `1px solid ${isActive ? t.primary : t.border}`, background: isActive ? t.primary : "transparent", color: isActive ? "#fff" : t.muted, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>
                        {menu.name}
                        {menu.menu_type === "daily" && menu.price > 0 && (
                          <span style={{ marginLeft: "8px", opacity: 0.8 }}>{menu.price.toFixed(2)} &euro;</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {allCategories.length > 1 && (
                <div data-rl-cat-tabs style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap", marginBottom: "40px" }} data-testid="category-tabs">
                  {allCategories.map(cat => {
                    const isActive = cat.category_id === (activeCategory || allCategories[0]?.category_id);
                    return (
                      <button key={cat.category_id} onClick={() => setActiveCategory(cat.category_id)} data-testid={`cat-tab-${cat.category_id}`}
                        style={{ padding: "10px 22px", borderRadius: "100px", border: `1px solid ${isActive ? t.catActive.bg : t.border}`, background: isActive ? t.catActive.bg : "transparent", color: isActive ? t.catActive.text : t.muted, fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>
                        {cat.icon && <span style={{ marginRight: "6px" }}>{cat.icon}</span>}{cat.name}
                      </button>
                    );
                  })}
                </div>
              )}
              {activeCatData?.dishes?.length > 0 ? (
                <div data-rl-dishes-grid style={{ display: "grid" }} data-testid="dishes-grid">
                  {activeCatData.dishes.map(dish => <DishCard key={dish.dish_id} dish={dish} t={t} onAllergenClick={openAllergenModal} />)}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "60px 20px", color: t.muted }}>
                  <UtensilsCrossed size={48} style={{ margin: "0 auto 16px", opacity: 0.3 }} />
                  <p>Sin platos activos en esta categoria</p>
                </div>
              )}
                </>
              )}
            </div>
          </section>
        );

        return null;
      })}

      {/* ── Contact Section ─────────────────────────────────────────────── */}
      {features.show_contact && (restaurant.address || restaurant.phone || restaurant.email) && (
        <section id="contacto" data-rl-contact style={{ background: t.sectionAlt }} data-testid="contact-section">
          <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "center" }}>
            <div style={{ display: "inline-block", width: "40px", height: "3px", background: t.primary, marginBottom: "20px", borderRadius: "2px" }} />
            <h2 style={{ fontFamily: t.heroFont, fontStyle: "italic", fontWeight: styleKey === "elegante" ? 400 : 300, fontSize: "clamp(1.8rem, 4vw, 2.8rem)", color: t.text, marginBottom: "48px" }}>
              Encuentranos
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "20px", alignItems: "center" }}>
              {restaurant.address && (
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: `${t.primary}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <MapPin size={20} color={t.primary} />
                  </div>
                  <span style={{ fontFamily: t.bodyFont, fontSize: "1rem", color: t.text, textAlign: "left" }}>{restaurant.address}</span>
                </div>
              )}
              {restaurant.phone && (
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: `${t.primary}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Phone size={20} color={t.primary} />
                  </div>
                  <a href={`tel:${restaurant.phone}`} style={{ fontFamily: t.bodyFont, fontSize: "1rem", color: t.primary, textDecoration: "none", fontWeight: 600 }}>{restaurant.phone}</a>
                </div>
              )}
              {restaurant.email && (
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: `${t.primary}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Mail size={20} color={t.primary} />
                  </div>
                  <a href={`mailto:${restaurant.email}`} style={{ fontFamily: t.bodyFont, fontSize: "1rem", color: t.primary, textDecoration: "none", fontWeight: 600 }}>{restaurant.email}</a>
                </div>
              )}
            </div>
            {showSchedule && <ScheduleSection schedule={content.schedule} t={t} />}
          </div>
        </section>
      )}

      {/* ── Loyalty Section ───────────────────────────────────────────── */}
      {features.show_loyalty && loyaltyInfo?.config && loyaltyInfo.rewards?.length > 0 && (
        <section id="fidelizacion" data-rl-loyalty style={{ background: t.bg }} data-testid="loyalty-section">
          <div style={{ maxWidth: "900px", margin: "0 auto" }}>
            <div style={{ display: "inline-block", width: "40px", height: "3px", background: t.primary, marginBottom: "20px", borderRadius: "2px" }} />
            <h2 style={{ fontFamily: t.heroFont, fontStyle: "italic", fontWeight: styleKey === "elegante" ? 400 : 300, fontSize: "clamp(1.8rem, 4vw, 2.8rem)", color: t.text, marginBottom: "12px" }}>
              {loyaltyInfo.config.program_name || "Club de Fidelizacion"}
            </h2>
            <p style={{ fontFamily: t.bodyFont, fontSize: "1rem", color: t.muted, marginBottom: "56px", maxWidth: "560px" }}>
              Unete a nuestro programa y acumula puntos en cada visita. Canjealos por recompensas exclusivas.
            </p>
            <div data-rl-loyalty-grid style={{ display: "grid", alignItems: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                  <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: `${t.primary}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: "1.2rem" }}>&#11088;</span>
                  </div>
                  <div>
                    <p style={{ fontFamily: t.heroFont, fontSize: "1rem", fontWeight: 600, color: t.text, marginBottom: "4px" }}>Acumula puntos</p>
                    <p style={{ fontFamily: t.bodyFont, fontSize: "0.9rem", color: t.muted }}>
                      Gana <strong style={{ color: t.primary }}>{loyaltyInfo.config.points_per_euro || 1} punto(s)</strong> por cada euro consumido
                    </p>
                  </div>
                </div>
                {loyaltyInfo.config.levels?.length > 0 && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                    <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: `${t.primary}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: "1.2rem" }}>&#127941;</span>
                    </div>
                    <div>
                      <p style={{ fontFamily: t.heroFont, fontSize: "1rem", fontWeight: 600, color: t.text, marginBottom: "8px" }}>Niveles exclusivos</p>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {loyaltyInfo.config.levels.map(l => (
                          <span key={l.name} style={{ padding: "3px 10px", borderRadius: "100px", fontSize: "0.75rem", fontWeight: 700, background: (l.color || "#888") + "25", color: l.color || "#888" }}>
                            {l.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {loyaltyInfo.rewards?.length > 0 && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                    <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: `${t.primary}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: "1.2rem" }}>&#127873;</span>
                    </div>
                    <div>
                      <p style={{ fontFamily: t.heroFont, fontSize: "1rem", fontWeight: 600, color: t.text, marginBottom: "8px" }}>Recompensas</p>
                      {loyaltyInfo.rewards.slice(0, 3).map(r => (
                        <p key={r.reward_id} style={{ fontFamily: t.bodyFont, fontSize: "0.85rem", color: t.muted, marginBottom: "4px" }}>
                          &middot; {r.name} <span style={{ color: t.primary, fontWeight: 600 }}>({r.points_required} pts)</span>
                        </p>
                      ))}
                      {loyaltyInfo.rewards.length > 3 && (
                        <button
                          type="button"
                          onClick={() => setShowAllRewards(true)}
                          data-testid="show-all-rewards-btn"
                          style={{
                            background: "transparent",
                            border: "none",
                            color: t.primary,
                            fontFamily: t.bodyFont,
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            padding: "4px 0",
                            marginTop: "4px",
                            cursor: "pointer",
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                          }}
                        >
                          Ver todas las recompensas ({loyaltyInfo.rewards.length}) &rarr;
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {loyaltyQR?.portal_url && (
                  <a href={loyaltyQR.portal_url} target="_blank" rel="noopener noreferrer"
                    style={{ display: "inline-block", padding: "14px 32px", borderRadius: "100px", background: t.primary, color: "#fff", fontFamily: t.bodyFont, fontWeight: 700, fontSize: "0.95rem", textDecoration: "none", marginTop: "8px", width: "fit-content" }}
                    data-testid="loyalty-portal-link">
                    Unete gratis &rarr;
                  </a>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
                {loyaltyQR?.qr_code ? (
                  <>
                    <div style={{ padding: "20px", background: "#fff", borderRadius: "20px", boxShadow: "0 8px 32px -8px rgba(0,0,0,0.14)", border: `1px solid ${t.border}` }}>
                      <img src={loyaltyQR.qr_code} alt="QR Programa Fidelizacion" style={{ width: "160px", height: "160px" }} data-testid="landing-loyalty-qr" />
                    </div>
                    <p style={{ fontFamily: t.bodyFont, fontSize: "0.85rem", color: t.muted, textAlign: "center", maxWidth: "200px" }}>
                      Escanea para registrarte en el programa
                    </p>
                  </>
                ) : (
                  <div style={{ width: "200px", height: "200px", background: t.sectionAlt, borderRadius: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ color: t.muted, fontSize: "0.85rem" }}>QR no disponible</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer style={{ background: t.navBg, padding: "40px 20px", textAlign: "center" }}>
        {socialAccounts.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
            {socialAccounts.map((a, i) => (
              <span key={`social-${a.platform}-${i}`} style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "6px 14px", borderRadius: "20px",
                background: `${t.navText}15`, color: `${t.navText}90`,
                fontSize: "0.8rem", fontFamily: t.bodyFont,
              }}>
                {a.platform === "instagram" && "Instagram"}
                {a.platform === "google_business" && "Google Business"}
                {a.platform === "tiktok" && "TikTok"}
                {!["instagram", "google_business", "tiktok"].includes(a.platform) && a.platform}
                <span style={{ fontSize: "0.7rem", opacity: 0.6 }}>{a.account_name}</span>
              </span>
            ))}
          </div>
        )}
        <p style={{ fontFamily: t.bodyFont, color: `${t.navText}80`, fontSize: "0.85rem", margin: 0 }}>
          {content.footer_tagline || `\u00A9 ${new Date().getFullYear()} ${restaurant.name}`}
        </p>
        {!hideBranding && (
          <p style={{ fontFamily: t.bodyFont, color: `${t.navText}40`, fontSize: "0.75rem", marginTop: "8px" }}>
            Powered by MenuGenius
          </p>
        )}
      </footer>

      {/* WhatsApp floating button */}
      {showWhatsapp && <WhatsAppButton phone={restaurant.phone} t={t} />}

      {/* Full rewards modal */}
      {showAllRewards && loyaltyInfo?.rewards && (
        <div
          onClick={() => setShowAllRewards(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 300,
            background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
          data-testid="all-rewards-overlay"
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: t.cardBg, borderRadius: "20px", maxWidth: "520px",
              width: "100%", maxHeight: "84vh", overflow: "auto",
              padding: "28px", boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
              border: `1px solid ${t.border}`,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
              <div>
                <p style={{ fontFamily: t.bodyFont, fontSize: "0.75rem", letterSpacing: "1.5px", color: t.muted, textTransform: "uppercase", marginBottom: "6px" }}>
                  Recompensas disponibles
                </p>
                <h3 style={{ fontFamily: t.heroFont, fontStyle: "italic", fontSize: "1.6rem", color: t.text, margin: 0 }}>
                  {loyaltyInfo.config.program_name || "Club de Fidelizacion"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAllRewards(false)}
                aria-label="Cerrar"
                data-testid="close-all-rewards-btn"
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  fontSize: "1.4rem", color: t.muted, lineHeight: 1, padding: 4,
                }}
              >
                &times;
              </button>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
              {[...loyaltyInfo.rewards]
                .sort((a, b) => (a.points_required || 0) - (b.points_required || 0))
                .map(r => (
                  <li
                    key={r.reward_id}
                    style={{
                      display: "flex", alignItems: "center", gap: "12px",
                      padding: "14px 16px", borderRadius: "14px",
                      background: t.sectionAlt, border: `1px solid ${t.border}`,
                    }}
                  >
                    <div style={{
                      width: "40px", height: "40px", borderRadius: "10px",
                      background: `${t.primary}1A`, color: t.primary,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "1.1rem", flexShrink: 0,
                    }}>
                      &#127873;
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontFamily: t.heroFont, fontSize: "0.95rem", fontWeight: 600, color: t.text, margin: 0 }}>
                        {r.name}
                      </p>
                      {r.description && (
                        <p style={{ fontFamily: t.bodyFont, fontSize: "0.82rem", color: t.muted, margin: "2px 0 0" }}>
                          {r.description}
                        </p>
                      )}
                    </div>
                    <span style={{
                      fontFamily: t.bodyFont, fontSize: "0.85rem", fontWeight: 700,
                      color: t.primary, whiteSpace: "nowrap",
                      padding: "6px 12px", borderRadius: "100px",
                      background: `${t.primary}14`,
                    }}>
                      {r.points_required} pts
                    </span>
                  </li>
                ))}
            </ul>
            {/* Customer progress nudge — shown when a customer token is cached */}
            {customerProgress && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "16px 18px",
                  borderRadius: "16px",
                  background: `linear-gradient(135deg, ${t.primary}14, ${t.primary}06)`,
                  border: `1px solid ${t.primary}33`,
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
                data-testid="customer-progress-nudge"
              >
                <div style={{
                  width: "42px", height: "42px", borderRadius: "12px",
                  background: t.primary, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.2rem", flexShrink: 0,
                }}>
                  &#127919;
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: t.bodyFont, fontSize: "0.7rem", letterSpacing: "1.2px", textTransform: "uppercase", color: t.muted, margin: "0 0 2px" }}>
                    {customerProgress.customer_name ? `Hola ${customerProgress.customer_name.split(" ")[0]}` : "Tu progreso"}
                  </p>
                  {customerProgress.next_reward ? (
                    <p style={{ fontFamily: t.heroFont, fontSize: "0.95rem", color: t.text, margin: 0, lineHeight: 1.4 }}>
                      Te quedan{" "}
                      <strong style={{ color: t.primary }}>
                        {(customerProgress.next_reward.points_required || 0) - customerProgress.points} puntos
                      </strong>
                      {" "}para canjear <strong>{customerProgress.next_reward.name}</strong>
                    </p>
                  ) : (
                    <p style={{ fontFamily: t.heroFont, fontSize: "0.95rem", color: t.text, margin: 0, lineHeight: 1.4 }}>
                      ¡Ya puedes canjear cualquier recompensa! Tienes{" "}
                      <strong style={{ color: t.primary }}>{customerProgress.points} puntos</strong>.
                    </p>
                  )}
                </div>
              </div>
            )}
            {loyaltyQR?.portal_url && (
              <a
                href={loyaltyQR.portal_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block", textAlign: "center",
                  marginTop: "20px", padding: "13px 24px",
                  borderRadius: "100px", background: t.primary, color: "#fff",
                  fontFamily: t.bodyFont, fontWeight: 700, fontSize: "0.9rem",
                  textDecoration: "none",
                }}
                data-testid="all-rewards-cta"
              >
                Unete y empieza a sumar &rarr;
              </a>
            )}
          </div>
        </div>
      )}

      {/* Allergen overlay modal */}
      {allergenModalDish && (
        <div
          onClick={() => setAllergenModalDish(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 300,
            background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
          data-testid="allergen-overlay"
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: t.cardBg, borderRadius: "16px", maxWidth: "420px",
              width: "100%", maxHeight: "80vh", overflow: "auto",
              padding: "24px", boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
              border: `1px solid ${t.border}`,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <h3 style={{ fontFamily: t.heroFont, fontSize: "1.1rem", color: t.text, margin: 0 }}>
                {allergenModalDish.name}
              </h3>
              <button onClick={() => setAllergenModalDish(null)} style={{ background: "none", border: "none", cursor: "pointer", color: t.muted, fontSize: "1.2rem" }}>&times;</button>
            </div>

            {(() => {
              const algData = allergenCache[allergenModalDish.dish_id];
              if (!algData) return (
                <div style={{ textAlign: "center", padding: "20px", color: t.muted }}>
                  <div style={{ width: "24px", height: "24px", border: `2px solid ${t.primary}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 10px" }} />
                  <p style={{ fontSize: "0.85rem" }}>Cargando alergenos...</p>
                </div>
              );

              const hasAny = (algData.contains?.length || 0) + (algData.may_contain?.length || 0) + (algData.traces?.length || 0);

              return (
                <div>
                  {/* Status */}
                  {(() => {
                    const isVerified = algData.review_status === "verified" || algData.review_status === "ai_verified";
                    return (
                  <div style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    padding: "8px 12px", borderRadius: "8px", marginBottom: "16px",
                    background: isVerified ? "#ecfdf580" : "#fef3c780",
                  }}>
                    {isVerified
                      ? <ShieldCheck size={14} color="#16a34a" />
                      : <AlertTriangle size={14} color="#ca8a04" />
                    }
                    <span style={{ fontSize: "0.75rem", color: isVerified ? "#16a34a" : "#ca8a04" }}>
                      {algData.review_status === "verified" ? "Verificado por el restaurante" : isVerified ? "Verificado" : "Calculo automatico"}
                    </span>
                  </div>
                    );
                  })()}

                  {/* Contains */}
                  {algData.contains?.length > 0 && (
                    <div style={{ marginBottom: "14px" }}>
                      <p style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#dc2626", marginBottom: "8px" }}>Contiene</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {algData.contains.map(id => {
                          const info = ALG_ICONS[id];
                          return info ? (
                            <span key={id} style={{
                              display: "inline-flex", alignItems: "center", gap: "5px",
                              padding: "4px 10px", borderRadius: "100px", fontSize: "0.75rem", fontWeight: 600,
                              background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
                            }}>
                              <AllergenDot algId={id} /> {info.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* May contain */}
                  {algData.may_contain?.length > 0 && (
                    <div style={{ marginBottom: "14px" }}>
                      <p style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#d97706", marginBottom: "8px" }}>Puede contener</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {algData.may_contain.map(id => {
                          const info = ALG_ICONS[id];
                          return info ? (
                            <span key={id} style={{
                              display: "inline-flex", alignItems: "center", gap: "5px",
                              padding: "4px 10px", borderRadius: "100px", fontSize: "0.75rem", fontWeight: 600,
                              background: "#fffbeb", border: "1px dashed #fcd34d", color: "#92400e",
                            }}>
                              <AllergenDot algId={id} severity="may_contain" /> {info.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {!hasAny && (
                    <div style={{ textAlign: "center", padding: "16px", color: t.muted }}>
                      <p style={{ fontSize: "0.85rem" }}>No se han detectado alergenos en este plato</p>
                    </div>
                  )}

                  {/* Disclaimer */}
                  <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: "12px", marginTop: "16px" }}>
                    <p style={{ fontSize: "0.65rem", color: t.muted, lineHeight: 1.5 }}>
                      Informacion basada en ingredientes y fichas registradas. Puede existir contaminacion cruzada en cocina. Consulte al restaurante si tiene una alergia severa.
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
